import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("nc_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// API-level ban enforcement lands as 403 "Account suspended" on every request —
// hard-banned sessions are marched to /banned the moment any call comes back.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err.response?.data?.detail;
    if (err.response?.status === 403 && typeof detail === "string" && detail.startsWith("Account suspended")) {
      try {
        const u = JSON.parse(localStorage.getItem("nc_user") || "{}");
        localStorage.setItem("nc_user", JSON.stringify({ ...u, banned: true, ban_reason: detail.replace("Account suspended — ", "") }));
      } catch { /* ignore */ }
      if (!window.location.pathname.startsWith("/banned") && !window.location.pathname.startsWith("/cef/")) {
        window.location.assign("/banned");
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function safeCopy(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      return true;
    } catch {
      return false;
    }
  }
}

export async function downloadAuthedFile(path, filename) {
  const token = localStorage.getItem("nc_token");
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let detail = "Download failed";
    try { detail = formatApiErrorDetail((await res.json()).detail); } catch { /* ignore */ }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export default api;
